// frontend/src/components/admin/content/ProjectFormModal.tsx

import { useState } from 'react';
import { adminService } from '../../../services/admin.service';
import { AdminProject } from '../../../types/admin.types';

interface ProjectFormModalProps {
  project?: AdminProject | null;
  onClose: () => void;
  onSaved: () => void;
}

const CATEGORIES = [
  { value: 'coding', label: '🐍 Kodlama & Algoritma' },
  { value: 'math', label: '📐 Matematik & Mantık' },
  { value: 'science', label: '🧪 Fen & Teknoloji' },
  { value: 'english', label: '🇬🇧 İngilizce' },
  { value: 'turkish', label: '📚 Türkçe & Edebiyat' },
  { value: 'game', label: '🎮 Oyun Programlama' },
  { value: 'ai_robotics', label: '🤖 Yapay Zeka & Robotik' },
];

export function ProjectFormModal({ project, onClose, onSaved }: ProjectFormModalProps) {
  const isEditing = Boolean(project?.id);

  const [title, setTitle] = useState(project?.title ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [category, setCategory] = useState(project?.category ?? 'coding');
  const [difficulty, setDifficulty] = useState<'beginner' | 'intermediate' | 'advanced'>(
    project?.difficulty ?? 'beginner'
  );
  const [starterCode, setStarterCode] = useState(
    project?.starter_code ?? '# Proje başlangıç kodu\nprint("Merhaba MinikCoder!")\n'
  );
  const [xpReward, setXpReward] = useState(project?.xp_reward ?? 100);
  const [estimatedDuration, setEstimatedDuration] = useState(project?.estimated_duration ?? '2 Saat');
  const [isPublished, setIsPublished] = useState(project?.is_published ?? true);
  const [isPremium, setIsPremium] = useState(project?.is_premium ?? false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError('Proje başlığı girilmelidir.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload: Partial<AdminProject> = {
        title: title.trim(),
        description: description.trim(),
        category,
        difficulty,
        starter_code: starterCode,
        xp_reward: Number(xpReward) || 100,
        estimated_duration: estimatedDuration.trim() || '2 Saat',
        is_published: isPublished,
        is_premium: isPremium,
      };

      if (isEditing && project?.id) {
        await adminService.updateProject(project.id, payload);
      } else {
        await adminService.createProject(payload);
      }

      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Proje kaydedilirken bir hata oluştu.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal" style={{ maxWidth: 650 }} onClick={(e) => e.stopPropagation()}>
        <header className="admin-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0 }}>{isEditing ? 'Projeyi Düzenle' : 'Yeni Proje Oluştur'}</h3>
          <button type="button" className="admin-remove-btn" onClick={onClose}>✕</button>
        </header>

        {error && <div className="quiz-error-banner" style={{ marginBottom: 16 }}>{error}</div>}

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label>Proje Başlığı *</label>
            <input
              type="text"
              className="input"
              placeholder="Örn: Basketbol Skor Takibi"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Görev Açıklaması</label>
            <textarea
              className="input"
              rows={3}
              placeholder="Öğrencinin bu projede ne yapması gerektiğine dair açıklama..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="grid grid-2" style={{ gap: 16 }}>
            <div className="form-group">
              <label>Ders / Kategori</label>
              <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label.replace(/[\uD800-\uDFFF\u2600-\u27BF]/g, '').trim()}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Zorluk Seviyesi</label>
              <select
                className="input"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as any)}
              >
                <option value="beginner">Başlangıç</option>
                <option value="intermediate">Orta</option>
                <option value="advanced">İleri</option>
              </select>
            </div>
          </div>

          <div className="grid grid-2" style={{ gap: 16 }}>
            <div className="form-group">
              <label>XP Ödülü</label>
              <input
                type="number"
                className="input"
                value={xpReward}
                onChange={(e) => setXpReward(Number(e.target.value))}
                min={10}
                max={1000}
              />
            </div>

            <div className="form-group">
              <label>Tahmini Süre</label>
              <input
                type="text"
                className="input"
                placeholder="Örn: 2 Saat"
                value={estimatedDuration}
                onChange={(e) => setEstimatedDuration(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Başlangıç Kodu (Python Starter Code)</label>
            <textarea
              className="input"
              rows={6}
              style={{ fontFamily: 'monospace', fontSize: 13, background: '#1e293b', color: '#e2e8f0' }}
              value={starterCode}
              onChange={(e) => setStarterCode(e.target.value)}
            />
          </div>

          <div className="grid grid-2" style={{ gap: 16, marginTop: 12 }}>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: 'var(--primary)' }}
                />
                <span>Projeyi Yayınla (Yayında)</span>
              </label>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={isPremium}
                  onChange={(e) => setIsPremium(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: 'var(--primary)' }}
                />
                <span>Premium Kilitli Proje (Üyelik Gerektirsin)</span>
              </label>
            </div>
          </div>

          <div className="admin-modal-actions" style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button type="button" className="lesson-btn secondary" onClick={onClose} disabled={isSubmitting}>
              İptal
            </button>
            <button type="submit" className="lesson-btn primary" disabled={isSubmitting}>
              {isSubmitting ? 'Kaydediliyor...' : isEditing ? 'Değişiklikleri Kaydet' : 'Proje Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
